import { useState } from "react";
import { PencilSimple, Plus, ToggleLeft, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader } from "@/components/common";
import { SUPPLIER_CATEGORIES, categoryIcon, categorySlug } from "@/lib/supplierCategories";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = {
  name: "",
  category: SUPPLIER_CATEGORIES[0].name,
  business: "",
  email: "",
  phone: "",
  website: "",
  instagram: "",
};

export default function StaffSuppliers() {
  const { data, loading, refetch } = useGet("/staff/preferred-suppliers");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const suppliers = data || [];

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (supplier) => {
    setEditing(supplier);
    setForm({ ...EMPTY, ...supplier, category: supplier.category || EMPTY.category });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Supplier name is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (editing) {
        await api.put(`/staff/preferred-suppliers/${editing.id}`, payload);
        toast.success("Preferred supplier updated.");
      } else {
        await api.post("/staff/preferred-suppliers", payload);
        toast.success("Preferred supplier created and published.");
      }
      setOpen(false);
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const updateState = async (supplier, payload, message) => {
    try {
      await api.put(`/staff/preferred-suppliers/${supplier.id}`, payload);
      toast.success(message);
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const deleteSupplier = async () => {
    if (!deleting) {
      return;
    }
    try {
      await api.delete(`/staff/preferred-suppliers/${deleting.id}`);
      toast.success("Preferred supplier deleted.");
      setDeleting(null);
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  if (loading) {
    return <Loading label="Loading preferred suppliers" />;
  }

  return (
    <div>
      <PageHeader
        eyebrow="Gledswood operations"
        title="Preferred Suppliers"
        description="Manage the venue directory clients can browse. Client supplier contacts remain event-specific."
      >
        <Button
          className="rounded-full bg-primary hover:bg-primary/90"
          onClick={openCreate}
          data-testid="create-preferred-supplier-btn"
        >
          <Plus size={18} className="mr-1" />
          Add supplier
        </Button>
      </PageHeader>

      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
        data-testid="staff-supplier-category-landing"
      >
        {SUPPLIER_CATEGORIES.map(({ name, icon: Icon }) => {
          const entries = suppliers.filter((item) => item.category === name);
          const published = entries.filter((item) => item.published !== false).length;
          return (
            <a
              key={name}
              href={`#${categorySlug(name)}`}
              className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 transition-colors duration-200 hover:border-secondary hover:bg-muted"
              data-testid={`staff-supplier-category-${categorySlug(name)}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50">
                <Icon size={17} className="text-secondary" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-primary">{name}</span>
                <span className="block mt-0.5 text-xs text-muted-foreground">
                  {entries.length} total · {published} live
                </span>
              </span>
            </a>
          );
        })}
      </div>

      <div className="mt-10 space-y-10">
        {SUPPLIER_CATEGORIES.map(({ name }) => (
          <CategorySection
            key={name}
            category={name}
            entries={suppliers.filter((item) => item.category === name)}
            onEdit={openEdit}
            onToggle={updateState}
            onDelete={setDeleting}
          />
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              {editing ? "Edit preferred supplier" : "Add preferred supplier"}
            </DialogTitle>
            <DialogDescription>
              Published entries are visible read-only to event-connected clients.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Supplier name">
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                data-testid="preferred-supplier-name-input"
              />
            </Field>
            <Field label="Category">
              <Select
                value={form.category}
                onValueChange={(value) => setForm({ ...form, category: value })}
              >
                <SelectTrigger data-testid="preferred-supplier-category-select">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLIER_CATEGORIES.map(({ name }) => (
                    <SelectItem key={name} value={name} data-testid={`category-option-${categorySlug(name)}`}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Business">
              <Input
                value={form.business}
                onChange={(event) => setForm({ ...form, business: event.target.value })}
                data-testid="preferred-supplier-business-input"
              />
            </Field>
            <Field label="Email">
              <Input
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                data-testid="preferred-supplier-email-input"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                data-testid="preferred-supplier-phone-input"
              />
            </Field>
            <Field label="Website">
              <Input
                value={form.website}
                onChange={(event) => setForm({ ...form, website: event.target.value })}
                data-testid="preferred-supplier-website-input"
              />
            </Field>
            <Field label="Instagram handle">
              <Input
                value={form.instagram}
                onChange={(event) => setForm({ ...form, instagram: event.target.value })}
                data-testid="preferred-supplier-instagram-input"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={save}
              disabled={busy}
              data-testid="save-preferred-supplier-btn"
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Create & publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(value) => !value && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this preferred supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.name || "This supplier"} will be removed from the staff directory and
              client view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive hover:bg-destructive/90"
              onClick={deleteSupplier}
              data-testid="confirm-delete-preferred-supplier"
            >
              Delete supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategorySection({ category, entries, onEdit, onToggle, onDelete }) {
  const Icon = categoryIcon(category);
  return (
    <section id={categorySlug(category)} data-testid={`staff-supplier-section-${categorySlug(category)}`}>
      <div className="flex items-end justify-between gap-4 border-b border-border pb-3">
        <h2 className="flex items-center gap-3 font-heading text-3xl text-primary">
          <Icon size={26} className="text-secondary" />
          {category}
        </h2>
        <span className="text-sm text-muted-foreground">{entries.length} listed</span>
      </div>
      {entries.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
          No suppliers in this category yet.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {entries.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={onEdit}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SupplierCard({ supplier, onEdit, onToggle, onDelete }) {
  return (
    <article
      className="rounded-lg border border-border bg-card p-5"
      data-testid={`staff-preferred-supplier-${supplier.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Badge variant="outline" className="rounded-full">
            {supplier.status === "retired"
              ? "Legacy record"
              : supplier.published !== false
                ? "Published"
                : "Unpublished"}
          </Badge>
          <h3 className="mt-3 break-words font-heading text-2xl text-primary">{supplier.name}</h3>
        </div>
        <button
          type="button"
          onClick={() => onEdit(supplier)}
          className="rounded-lg p-2 text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-primary"
          data-testid={`edit-preferred-supplier-${supplier.id}`}
          aria-label={`Edit ${supplier.name}`}
        >
          <PencilSimple size={18} />
        </button>
      </div>
      <div className="mt-3 space-y-1 text-sm text-muted-foreground">
        {supplier.business && <p className="break-words">{supplier.business}</p>}
        {supplier.email && <p className="break-words">{supplier.email}</p>}
        {supplier.phone && <p>{supplier.phone}</p>}
        {supplier.website && <p className="break-words">{supplier.website}</p>}
        {supplier.instagram && <p>@{supplier.instagram}</p>}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {supplier.status !== "retired" && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() =>
              onToggle(
                supplier,
                { published: !supplier.published },
                supplier.published ? "Supplier unpublished." : "Supplier published.",
              )
            }
            data-testid={`toggle-preferred-supplier-${supplier.id}`}
          >
            <ToggleLeft size={16} className="mr-1" />
            {supplier.published ? "Unpublish" : "Publish"}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="rounded-full text-destructive"
          onClick={() => onDelete(supplier)}
          data-testid={`delete-preferred-supplier-${supplier.id}`}
        >
          <Trash size={16} className="mr-1" />
          Delete
        </Button>
      </div>
    </article>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
